/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:22:48 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { BillingConfiguration, BillingSchedule, BillingScheduleValidationSchema } from "lib/schemas";
import { getNewBillingScheduleDefault } from "lib/factories";
import { TabulatedOne2ManyInlineInput, TextFormInput, CheckBoxFormInput } from "ui-widgets/forms";
import { APISERVICEPATHS } from "lib/api";
import { useOSFormContext } from "ui-widgets/forms/providers";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import { PriceSettings } from "./PriceSettings";
import { PricingChipButton } from "./PricingChipButton";

export const BillingScheduleList = () => {
  const { watchedCurrency, watchedBillingConfigurationId, getNewBillingSchedule } = useBillingScheduleList();

  return (
    <TabulatedOne2ManyInlineInput<BillingConfiguration, BillingSchedule>
      apiServicePath={APISERVICEPATHS.billingSchedules}
      foreignKey="billing_configuration"
      foreignKeyId={watchedBillingConfigurationId}
      uniqueIdentifier="id"
      getNewItemDefaultValues={getNewBillingSchedule}
      relatedObjectValidationSchema={BillingScheduleValidationSchema}
      headerCells={[
        <Typography variant="subtitle2">Fee Name</Typography>,
        <Typography variant="subtitle2">Fee Code</Typography>,
        <Typography variant="subtitle2">Price ({watchedCurrency})</Typography>,
        <Typography variant="subtitle2">Allow Price Editing During Workflow</Typography>,
      ]}
      renderBodyCells={({ rowItem: billingSchedule }) => [
        <Typography>{billingSchedule.name}</Typography>,
        <Typography>{billingSchedule.code}</Typography>,

        <Typography>
          <PricingChipButton price_settings={billingSchedule.price_settings} />
        </Typography>,
        <Checkbox disabled checked={billingSchedule.reviewers_can_edit_price} />,
      ]}
      renderInlineFields={(billingSchedule) => [
        <TextFormInput<BillingSchedule> schema_field_key="name" variant="standard" />,
        <TextFormInput<BillingSchedule> schema_field_key="code" variant="standard" />,
        <PriceSettings />,
        <CheckBoxFormInput<BillingSchedule> schema_field_key="reviewers_can_edit_price" label="" />,
      ]}
    />
  );
};

const useBillingScheduleList = () => {
  const { watch } = useOSFormContext<BillingConfiguration>();

  const [watchedBillingConfigurationId, watchedBillingOrgId, watchedCurrency] = watch<(keyof BillingConfiguration)[]>([
    "id",
    "artifact_org",
    "currency",
  ]);

  const getNewBillingSchedule = () => getNewBillingScheduleDefault(watchedBillingOrgId, watchedBillingConfigurationId);

  return { watchedBillingConfigurationId, watchedCurrency, getNewBillingSchedule };
};
